import React, { useState, useEffect, useRef, useContext } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faRobot, faPlay, faStop, faTrophy, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { SettingsContext } from '../contexts/SettingsContext';
import { SupervisorEngine } from '../services/supervisor-engine';
import LogViewer from './LogViewer';

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  background-color: var(--surface-color);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  font-family: var(--font-family);
  font-weight: 400;
`;

const BriefInput = styled.div`
  padding: var(--space-8);
  background: rgba(255, 255, 255, 0.8);
  border-radius: var(--radius-xl);
  margin: var(--space-4);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  h2 {
    margin-bottom: var(--space-4);
    color: var(--text-color);
    font-family: var(--font-family);
  font-weight: 400;
    font-size: var(--text-xl);
    font-weight: 600;
  }

  p {
    margin-bottom: var(--space-4);
    color: var(--text-secondary);
    font-family: var(--font-family);
  font-weight: 400;
    font-size: var(--text-base);
    line-height: 1.6;
  }
`;

const BriefTextarea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: var(--space-4);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-family: var(--font-family);
  font-weight: 400;
  background: rgba(255, 255, 255, 0.9);
  color: var(--text-color);
  resize: vertical;
  direction: rtl;
  line-height: 1.6;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100), var(--shadow-md);
    transform: translateY(-1px);
  }

  &:hover:not(:focus) {
    border-color: var(--primary-300);
  }

  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
  }
`;

const StartButton = styled.button`
  width: 100%;
  padding: var(--space-4) var(--space-6);
  background: rgba(74, 144, 226, 0.9);
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  font-size: var(--text-lg);
  font-weight: 300;
  font-family: var(--font-heading-regular);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.4);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    border-radius: inherit;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover:not(:disabled) {
    background: var(--primary-700);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(90, 99, 230, 0.4);

    &::before {
      opacity: 1;
    }
  }

  &:disabled {
    background: var(--neutral-300);
    color: var(--text-secondary);
    cursor: not-allowed;
    transform: none;
    box-shadow: var(--shadow-sm);

    &::before {
      display: none;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(90, 99, 230, 0.3);
    transition: all 0.1s ease;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }

  /* Pulse effect when there's text */
  ${props => props.hasText && `
    box-shadow: 0 2px 8px rgba(90, 99, 230, 0.3), 0 0 0 1px rgba(90, 99, 230, 0.2);
    animation: subtlePulse 2s ease-in-out infinite;

    @keyframes subtlePulse {
      0%, 100% {
        box-shadow: 0 2px 8px rgba(90, 99, 230, 0.3), 0 0 0 1px rgba(90, 99, 230, 0.2);
      }
      50% {
        box-shadow: 0 4px 12px rgba(90, 99, 230, 0.4), 0 0 0 2px rgba(90, 99, 230, 0.3);
      }
    }
  `}
`;

const AgentCard = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-lg);
  padding: var(--space-6);
  margin-bottom: var(--space-6);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-right: 4px solid ${props => props.borderColor};
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  font-family: var(--font-family);
  font-weight: 400;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  
  &.thinking {
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.8; }
    100% { opacity: 1; }
  }
`;

const AgentHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const AgentIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.bgColor}40;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  margin-left: 1rem;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const AgentInfo = styled.div`
  flex: 1;
`;

const AgentName = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 500;
  color: var(--text-color);
  font-family: var(--font-heading-bold);
`;

const AgentRole = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const AgentContent = styled.div`
  color: var(--text-color);
  line-height: 1.6;

  h2 {
    font-size: 1.25rem;
    margin: 1rem 0 0.5rem;
    color: var(--primary-color);
    font-family: var(--font-heading-medium);
    font-weight: 400;
  }

  h3 {
    font-size: 1.125rem;
    margin: 0.75rem 0 0.5rem;
    color: var(--text-color);
    font-family: var(--font-heading-medium);
    font-weight: 400;
  }
  
  ul, ol {
    margin: 0.5rem 0;
    padding-right: 1.5rem;
  }
  
  li {
    margin: 0.25rem 0;
  }
  
  strong {
    font-weight: 600;
    color: var(--primary-color);
  }
`;

const ThinkingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  
  .loader {
    width: 20px;
    height: 20px;
    border: 2px solid var(--border-color);
    border-top: 2px solid var(--primary-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const UserInputContainer = styled.div`
  background: rgba(255, 255, 255, 0.8);
  border-radius: 1rem;
  padding: 1.5rem;
  margin: 1rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border-right: 4px solid #22c55e;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  
  h3 {
    margin-bottom: 1rem;
    color: var(--text-color);
  }
`;

const UserResponseTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: var(--input-background);
  color: var(--text-color);
  resize: vertical;
  font-family: 'Assistant', sans-serif;
  
  &:focus {
    outline: none;
    border-color: #22c55e;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  background: rgba(74, 144, 226, 0.9);
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-weight: 300;
  font-family: var(--font-heading-regular);
  cursor: pointer;
  margin-top: var(--space-4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.4);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    border-radius: inherit;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover:not(:disabled) {
    background: var(--primary-700);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(90, 99, 230, 0.4);

    &::before {
      opacity: 1;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(90, 99, 230, 0.3);
    transition: all 0.1s ease;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }

  &:disabled {
    background: var(--neutral-300);
    color: var(--text-secondary);
    cursor: not-allowed;
    box-shadow: var(--shadow-sm);
    transform: none;

    &::before {
      display: none;
    }
  }

  /* Pulse effect when there's text */
  ${props => props.hasText && `
    box-shadow: 0 2px 8px rgba(90, 99, 230, 0.3), 0 0 0 1px rgba(90, 99, 230, 0.2);
    animation: subtlePulse 2s ease-in-out infinite;

    @keyframes subtlePulse {
      0%, 100% {
        box-shadow: 0 2px 8px rgba(90, 99, 230, 0.3), 0 0 0 1px rgba(90, 99, 230, 0.2);
      }
      50% {
        box-shadow: 0 4px 12px rgba(90, 99, 230, 0.4), 0 0 0 2px rgba(90, 99, 230, 0.3);
      }
    }
  `}
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
  color: var(--text-secondary);
  
  .loader {
    width: 30px;
    height: 30px;
    border: 3px solid var(--border-color);
    border-top: 3px solid var(--primary-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
`;

const CompletionCard = styled.div`
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.6) 0%, rgba(245, 158, 11, 0.4) 100%);
  color: white;
  border-radius: var(--radius-xl);
  padding: var(--space-8);
  text-align: center;
  margin: var(--space-8) 0;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  font-family: var(--font-family);
  font-weight: 400;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  .trophy {
    font-size: var(--text-4xl);
    margin-bottom: var(--space-4);
  }

  h2 {
    margin: var(--space-2) 0;
    font-size: var(--text-2xl);
    font-family: var(--font-family);
  font-weight: 400;
    font-weight: 600;
  }

  p {
    margin: var(--space-2) 0 var(--space-6);
    opacity: 0.95;
    font-size: var(--text-base);
    line-height: 1.6;
  }
`;

const ResetButton = styled.button`
  padding: var(--space-3) var(--space-8);
  background: white;
  color: var(--warning-600);
  border: none;
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-weight: 600;
  font-family: var(--font-family);
  font-weight: 400;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid var(--warning-600);
    outline-offset: 2px;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: var(--space-8);
  font-family: var(--font-family);
  font-weight: 400;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(0.5px);
  -webkit-backdrop-filter: blur(0.5px);
`;

const ControlPanel = styled.div`
  padding: var(--space-4);
  background: rgba(255, 255, 255, 0.1);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: center;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
`;

const StopButton = styled.button`
  padding: var(--space-3) var(--space-8);
  background: linear-gradient(135deg, var(--error-600) 0%, var(--error-500) 100%);
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-weight: 600;
  font-family: var(--font-family);
  font-weight: 400;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%);
    border-radius: inherit;
    opacity: 0;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }

  &:hover {
    background: linear-gradient(135deg, var(--error-500) 0%, var(--error-600) 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);

    &::before {
      opacity: 1;
    }
  }

  &:focus-visible {
    outline: 2px solid var(--error-600);
    outline-offset: 2px;
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
  }
`;

// Agent class colors mapping
const agentColors = {
  supervisor: '#6366f1',
  strategist: '#ec4899',
  copywriter: '#f59e0b',
  seo_specialist: '#10b981',
  ppc_manager: '#3b82f6',
  social_media_manager: '#ef4444',
  data_analyst: '#8b5cf6',
  graphic_designer: '#d946ef',
  client_manager: '#14b8a6',
  user_input: '#22c55e'
};

function SupervisorChatWindow({ addLogEntry, addApiLog }) {
  const { 
    characters, 
    settings, 
    supervisorState, 
    setSupervisorState,
    textDirection 
  } = useContext(SettingsContext);
  
  const [brief, setBrief] = useState('');
  const [userResponse, setUserResponse] = useState('');
  const [engine, setEngine] = useState(null);
  const [logs, setLogs] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Initialize the supervisor engine
    const newEngine = new SupervisorEngine(
      characters,
      settings,
      (state) => {
        setSupervisorState(state);
        
        // Add log entry when state changes
        if (state.currentTask) {
          const logData = {
            timestamp: new Date().toISOString(),
            agent: state.currentTask.agent,
            action: state.currentTask.action,
            prompt: state.currentTask.prompt || '',
            response: state.currentTask.response || null,
            model: settings.model,
            temperature: settings.temperature,
            maxTokens: settings.maxTokens,
            metadata: {
              taskType: state.currentTask.type,
              isComplete: state.currentTask.isComplete
            }
          };
          setLogs(prev => [...prev, logData]);
        }
      }
    );
    setEngine(newEngine);
  }, [characters, settings, setSupervisorState]);

  useEffect(() => {
    scrollToBottom();
  }, [supervisorState.conversationHistory]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleStart = async () => {
    if (!brief.trim()) {
      alert('אנא הכנס בריף לפרויקט');
      return;
    }
    
    if (engine) {
      setLogs([]); // Clear previous logs
      await engine.start(brief);
      setBrief('');
    }
  };

  const handleUserResponse = async () => {
    if (!userResponse.trim()) {
      alert('אנא הכנס תשובה');
      return;
    }
    
    if (engine) {
      await engine.submitUserResponse(userResponse);
      setUserResponse('');
    }
  };

  const handleStop = () => {
    if (engine) {
      engine.stop();
    }
  };

  const handleReset = () => {
    if (engine) {
      engine.reset();
      setBrief('');
      setUserResponse('');
      setLogs([]);
    }
  };

  const renderAgentCard = (entry, index) => {
    const agent = characters.find(c => c.id === entry.agent);
    const agentColor = getAgentColor(agent?.agentClass || 'default');
    const isThinking = entry.isThinking;
    
    // Find the log entry for this conversation entry
    const logEntry = logs.find(log => 
      log.agent === entry.agent && 
      log.timestamp && 
      new Date(log.timestamp).getTime() > Date.now() - 60000 // Within last minute
    );

    return (
      <AgentCard key={index} className={`agent-card ${isThinking ? 'thinking' : ''}`} borderColor={agentColor}>
        <AgentHeader>
          <AgentIcon bgColor={agentColor}>
            <FontAwesomeIcon icon={agent?.icon || faRobot} />
          </AgentIcon>
          <AgentInfo>
            <AgentName>{agent?.name || entry.agent}</AgentName>
            <AgentRole>{agent?.role || ''}</AgentRole>
          </AgentInfo>
        </AgentHeader>
        
        <AgentContent>
          {isThinking ? (
            <ThinkingIndicator>
              <div className="loader"></div>
              <span>חושב...</span>
            </ThinkingIndicator>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: formatContent(entry.content) }} />
          )}
        </AgentContent>
        
        {logEntry && <LogViewer logData={logEntry} />}
      </AgentCard>
    );
  };

  const formatContent = (content) => {
    if (!content) return '';
    
    // Format markdown-like content to HTML
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/### (.*?)(?:\n|$)/g, '<h3>$1</h3>')
      .replace(/## (.*?)(?:\n|$)/g, '<h2>$1</h2>')
      .replace(/^- (.*?)(?:\n|$)/gm, '<li>$1</li>')
      .replace(/\n/g, '<br>');
  };

  const getAgentColor = (agentClass) => {
    const colors = {
      supervisor: '#6366f1',
      strategist: '#ec4899',
      copywriter: '#f59e0b',
      seo_specialist: '#10b981',
      ppc_manager: '#3b82f6',
      social_media_manager: '#ef4444',
      data_analyst: '#8b5cf6',
      graphic_designer: '#d946ef',
      client_manager: '#14b8a6',
      growth_hacker: '#22c55e',
      programmatic: '#f97316',
      video_producer: '#06b6d4',
      email_marketing: '#a855f7',
      default: '#6b7280'
    };
    return colors[agentClass] || colors.default;
  };

  return (
    <ChatContainer>
      {!supervisorState.isRunning && supervisorState.conversationHistory.length === 0 && (
        <BriefInput>
          <h2 style={{
            fontFamily: 'var(--font-heading-bold)',
            marginBottom: 'var(--space-4)',
            color: 'var(--text-color)',
            fontSize: 'var(--text-xl)',
            fontWeight: '500'
          }}>התחל פרויקט חדש</h2>
          <p style={{
            fontFamily: 'var(--font-heading-regular)',
            marginBottom: 'var(--space-4)',
            color: 'var(--text-secondary)',
            fontSize: 'var(--text-base)',
            lineHeight: '1.6',
            fontWeight: '300'
          }}>תאר את המוצר/שירות והמטרות שלך:</p>
          <BriefTextarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            placeholder="לדוגמה: 'יש לי חברה שמוכרת סנני מים וברי מים, קוראים לה Momento...'"
            direction="rtl"
          />
          <StartButton onClick={handleStart} disabled={supervisorState.isRunning} hasText={!!brief.trim()}>
            <FontAwesomeIcon icon={faPlay} />
            התחל ישיבת צוות
          </StartButton>
        </BriefInput>
      )}

      {supervisorState.waitingForUser && (
        <UserInputContainer>
          <h3 style={{
            fontFamily: 'var(--font-heading-medium)',
            marginBottom: 'var(--space-4)',
            color: 'var(--text-color)',
            fontSize: 'var(--text-lg)',
            fontWeight: '400'
          }}>{supervisorState.userQuestion}</h3>
          <UserResponseTextarea
            value={userResponse}
            onChange={(e) => setUserResponse(e.target.value)}
            placeholder="הכנס את תשובתך כאן..."
            direction="rtl"
          />
          <SubmitButton onClick={handleUserResponse} hasText={!!userResponse.trim()}>
            שלח תשובה
          </SubmitButton>
        </UserInputContainer>
      )}

      <MessagesContainer>
        {supervisorState.conversationHistory.map((entry, index) => renderAgentCard(entry, index))}
        
        {supervisorState.isRunning && !supervisorState.waitingForUser && (
          <LoadingIndicator>
            <div className="loader"></div>
            <span>מעבד...</span>
          </LoadingIndicator>
        )}
        
        {supervisorState.conversationHistory.length > 0 && 
         supervisorState.conversationHistory[supervisorState.conversationHistory.length - 1].type === 'completion' && (
          <CompletionCard>
            <FontAwesomeIcon icon={faTrophy} className="trophy" />
            <h2 style={{
              fontFamily: 'var(--font-heading-bold)',
              margin: 'var(--space-2) 0',
              fontSize: 'var(--text-2xl)',
              fontWeight: '500'
            }}>ישיבת הצוות הסתיימה בהצלחה!</h2>
            <p style={{
              fontFamily: 'var(--font-heading-regular)',
              margin: 'var(--space-2) 0 var(--space-6)',
              opacity: '0.95',
              fontSize: 'var(--text-base)',
              lineHeight: '1.6',
              fontWeight: '300'
            }}>האסטרטגיה מוכנה ומפורטת למעלה</p>
            <ResetButton onClick={handleReset} style={{
              fontFamily: 'var(--font-heading-regular)',
              fontSize: 'var(--text-base)',
              fontWeight: '300'
            }}>
              התחל פרויקט חדש
            </ResetButton>
          </CompletionCard>
        )}
        
        <div ref={messagesEndRef} />
      </MessagesContainer>

      {supervisorState.isRunning && (
        <ControlPanel>
          <StopButton onClick={handleStop} style={{
            fontFamily: 'var(--font-heading-regular)',
            fontSize: 'var(--text-base)',
            fontWeight: '300'
          }}>
            <FontAwesomeIcon icon={faStop} />
            עצור
          </StopButton>
        </ControlPanel>
      )}
    </ChatContainer>
  );
}

export default SupervisorChatWindow;
